import { useAccountingCompany } from '../components/HoldingAccounting';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function FinancialOverview() {
  const routerNavigate = useNavigate();
  const navigate = path => routerNavigate(window.location.pathname.startsWith('/holding/finance') ? path.replace('/company/finance', '/holding/finance') : path);
  const companyId = useAccountingCompany();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  useEffect(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/finance/overview`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : {})
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId, API]);

  if (initialLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!data) return <div style={{ padding: 40 }}>No data available</div>;

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  const cardStyle = { background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flex: '1 1 200px', minWidth: 180 };
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 24px', fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>💰 Financial Overview</h2>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Expected Revenue</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#22c55e' }}>{fmt(data.expected_revenue)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Collected Revenue</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(data.collected_revenue)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Outstanding Receivables</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f59e0b' }}>{fmt(data.outstanding_receivables)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #ef4444' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Total Expenses</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#ef4444' }}>{fmt(data.total_expenses)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #f97316' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Outstanding Payables</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f97316' }}>{fmt(data.outstanding_payables)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Net Cash</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: data.net_cash >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(data.net_cash)}</div>
        </div>
      </div>

      {/* Due Payments & Collections Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Incoming Due */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📥 Incoming Payments</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Due Today</span>
              <span style={{ fontWeight: 600, color: '#f59e0b' }}>{data.incoming_due_today}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Due This Week</span>
              <span style={{ fontWeight: 600 }}>{data.incoming_due_week}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Overdue</span>
              <span style={{ fontWeight: 600, color: '#ef4444' }}>{data.incoming_overdue}</span>
            </div>
          </div>
        </div>

        {/* Outgoing Due */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📤 Outgoing Payments</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Due Today</span>
              <span style={{ fontWeight: 600, color: '#f59e0b' }}>{data.outgoing_due_today}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Due This Week</span>
              <span style={{ fontWeight: 600 }}>{data.outgoing_due_week}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Overdue</span>
              <span style={{ fontWeight: 600, color: '#ef4444' }}>{data.outgoing_overdue}</span>
            </div>
          </div>
        </div>

        {/* Collections */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📊 Collections This Month</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Due</span>
              <span style={{ fontWeight: 600 }}>{fmt(data.collections?.due_this_month)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Collected</span>
              <span style={{ fontWeight: 600, color: '#22c55e' }}>{fmt(data.collections?.collected_this_month)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Collection %</span>
              <span style={{ fontWeight: 600 }}>{data.collections?.percentage || 0}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Overdue Amount</span>
              <span style={{ fontWeight: 600, color: '#ef4444' }}>{fmt(data.collections?.overdue_amount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      {Array.isArray(data.categories) && data.categories.length > 0 && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📂 Category Summary</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Projects</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Expected Revenue</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Collected</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Outstanding</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Expenses</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Net Cash</th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/finance/projects?category_id=${c.id}`)}>
                    <td style={tdStyle}>{c.name}</td>
                    <td style={tdStyle}>{c.projects_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(c.expected_revenue)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#22c55e' }}>{fmt(c.collected_revenue)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#f59e0b' }}>{fmt(c.outstanding_receivables)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#ef4444' }}>{fmt(c.expenses)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: c.net_cash >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(c.net_cash)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📥 Recent Incoming</h3>
          {Array.isArray(data.recent_incoming) && data.recent_incoming.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.recent_incoming.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 6 }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{t.payer || 'N/A'}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t.project} • {t.date}</div>
                  </div>
                  <div style={{ fontWeight: 600, color: '#22c55e' }}>{fmt(t.amount)}</div>
                </div>
              ))}
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No recent transactions</div>}
        </div>

        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📤 Recent Outgoing</h3>
          {Array.isArray(data.recent_outgoing) && data.recent_outgoing.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.recent_outgoing.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 6 }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{t.payee || 'N/A'}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t.project} • {t.date}</div>
                  </div>
                  <div style={{ fontWeight: 600, color: '#ef4444' }}>{fmt(t.amount)}</div>
                </div>
              ))}
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No recent transactions</div>}
        </div>
      </div>

      {/* Highest Outstanding */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>⚠️ Highest Outstanding Receivables</h3>
          {Array.isArray(data.highest_receivables) && data.highest_receivables.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr><th style={thStyle}>Payer</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Outstanding</th><th style={thStyle}>Due Date</th></tr>
                </thead>
                <tbody>
                  {data.highest_receivables.map(r => (
                    <tr key={r.id}><td style={tdStyle}>{r.payer}</td><td style={tdStyle}>{r.project}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#f59e0b' }}>{fmt(r.outstanding)}</td><td style={tdStyle}>{r.due_date ? new Date(r.due_date).toLocaleDateString() : '-'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No outstanding receivables</div>}
        </div>

        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>⚠️ Highest Outstanding Payables</h3>
          {Array.isArray(data.highest_payables) && data.highest_payables.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr><th style={thStyle}>Payee</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Outstanding</th><th style={thStyle}>Due Date</th></tr>
                </thead>
                <tbody>
                  {data.highest_payables.map(p => (
                    <tr key={p.id}><td style={tdStyle}>{p.payee}</td><td style={tdStyle}>{p.project}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#f97316' }}>{fmt(p.outstanding)}</td><td style={tdStyle}>{p.due_date ? new Date(p.due_date).toLocaleDateString() : '-'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No outstanding payables</div>}
        </div>
      </div>

      {/* Quick Access */}
      <div style={{ ...sectionStyle, marginTop: 20 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🚀 Quick Access</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'New Receivable', path: '/company/finance/receivables', icon: '📥' },
            { label: 'New Payable', path: '/company/finance/payables', icon: '📤' },
            { label: 'Record Transaction', path: '/company/finance/transactions', icon: '💳' },
            { label: 'Due Payments', path: '/company/finance/due-payments', icon: '📅' },
            { label: 'Reports', path: '/company/finance/reports', icon: '📊' },
            { label: 'Settings', path: '/company/finance/settings', icon: '⚙️' },
          ].map(q => (
            <button key={q.path} onClick={() => navigate(q.path)} style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              {q.icon} {q.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
